import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { Opportunity } from "../../types";
import { Archive, AlertCircle } from "lucide-react";

interface ArchiveOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity | null;
  onConfirmArchive: (reason: string, notes?: string) => Promise<void>;
}

const PRESET_REASONS = [
  "RFP Cancelled by Issuing Organization",
  "Client Retracted / Budget Cut",
  "Scope Changed / Unfavorable Contract Terms",
  "Withdrawn Early by MLC",
  "Disqualified Early / Mandatory Criteria Unmet",
  "Shelved / Past Due",
  "Duplicate Opportunity Record",
  "Other Reason",
];

export const ArchiveOpportunityModal: React.FC<ArchiveOpportunityModalProps> = ({
  isOpen,
  onClose,
  opportunity,
  onConfirmArchive,
}) => {
  const [reason, setReason] = useState<string>(PRESET_REASONS[0] ?? "Other Reason");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!opportunity) return;

    setSubmitting(true);
    setError(null);
    try {
      await onConfirmArchive(reason, notes.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to archive opportunity.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="md"
      title="Archive / End Opportunity Early"
      description="Remove this procurement opportunity from the active pipeline while preserving all audit history."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
            {error}
          </div>
        )}

        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold block">{opportunity?.title}</span>
            <span className="text-amber-700 dark:text-amber-400">
              Archiving ends the active proposal cycle and moves it to the Archived view. You can
              restore it to an active stage at any time.
            </span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
            Reason for Ending Early / Archiving <span className="text-rose-500">*</span>
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          >
            {PRESET_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
            Additional Context / Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Add relevant notes (e.g., client notified via email, RFP postponed to Q3, pricing conflict)..."
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 resize-none"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            <Archive className="h-3.5 w-3.5" />
            <span>{submitting ? "Archiving..." : "Archive Opportunity"}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
